import { createContext, useContext, useEffect, useState } from 'react';

import { useBsp } from 'libs/hooks/useBsp';
import { useConsentAnalytics, useSegmentAnalytics } from '@sothebys/analytics';
import { useDatadogRum } from 'libs/hooks/useDatadogRum';

export const isBrowser = () => typeof window !== 'undefined';
export const getEnv = key => (isBrowser() ? window.discoveryEnv : process.env)[key];
export const isProd = () => getEnv('TIER') === 'prod';

export const EnvContext = createContext<typeof window.discoveryEnv | null>(null);

type EnvContextProviderProps = {
  children: React.ReactNode;
};

export const EnvContextProvider = ({ children }: EnvContextProviderProps) => {
  const [env, setEnv] = useState<typeof window.discoveryEnv | null>(null);

  useEffect(() => {
    setEnv(window.discoveryEnv);
  }, []);

  useBsp(env); // Import BSP scripts that renders the header and footer
  useConsentAnalytics(
    env && {
      segmentWriteKey: env.SEGMENT_WRITE_KEY,
      oneTrustKey: env.ONETRUST_KEY,
      enableOneTrust:
        isBrowser() && window.location.hostname !== 'discovery-origin.prod.sothelabs.com',
    },
  );
  useSegmentAnalytics(env && { segmentWriteKey: env.SEGMENT_WRITE_KEY });
  useDatadogRum(env);

  return <EnvContext.Provider value={{ ...env }}>{children}</EnvContext.Provider>;
};

export const useEnv = (): typeof window.discoveryEnv => {
  const env = useContext(EnvContext);
  return env;
};
